import { UnsavedChangesDialog } from "@centric/ui/UnsavedChangesDialog";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useBlocker } from "react-router";

/** Confirm-dialog copy a guard source can supply for its own unsaved state. */
export interface UnsavedChangesCopy {
  title?: string;
  description?: string;
}

interface RegistryEntry extends UnsavedChangesCopy {
  dirty: boolean;
}

interface UnsavedChangesRegistry {
  /** Register/update a source's dirty state (pass `null` to unregister). */
  set: (id: string, entry: RegistryEntry | null) => void;
}

// Default no-op so `useUnsavedChangesGuard` is safe when rendered without a
// provider (e.g. an isolated unit test that renders a guarded component alone).
const UnsavedChangesContext = createContext<UnsavedChangesRegistry>({ set: () => {} });

export const useUnsavedChangesRegistry = () => useContext(UnsavedChangesContext);

/**
 * Owns the single React Router navigation blocker for the whole app.
 *
 * React Router supports only ONE `useBlocker` at a time (by design — see its
 * ADR-0001). Multiple guards each calling `useBlocker` warn and silently drop
 * all but one, so any component that wants to guard unsaved changes registers
 * its dirty state here via `useUnsavedChangesGuard` instead. This provider
 * aggregates every registered source into one blocker + one confirm dialog and
 * one `beforeunload` handler, and shows the copy of the first dirty source.
 */
export const UnsavedChangesProvider = ({ children }: { children: ReactNode }) => {
  const [entries, setEntries] = useState<Map<string, RegistryEntry>>(() => new Map());

  const set = useCallback((id: string, entry: RegistryEntry | null) => {
    setEntries((prev) => {
      const isDirty = entry?.dirty ?? false;

      // Nothing to change: not tracked and still clean.
      if (!isDirty && !prev.has(id)) {
        return prev;
      }

      const next = new Map(prev);

      if (entry && isDirty) {
        next.set(id, entry);
      } else {
        next.delete(id);
      }

      return next;
    });
  }, []);

  // Stable, so a dirty flip re-renders this provider and its dialog, not every registered page.
  const registry = useMemo(() => ({ set }), [set]);

  const anyDirty = entries.size > 0;
  const dirtyCopy = entries.values().next().value;

  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) =>
        anyDirty && currentLocation.pathname !== nextLocation.pathname,
      [anyDirty],
    ),
  );

  // Hard reload / tab close guard, active only while something is dirty.
  useEffect(() => {
    if (!anyDirty) {
      return;
    }

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      // `preventDefault()` alone is the modern trigger for the confirmation
      // dialog and is honoured by every browser this app targets. The old
      // `event.returnValue = ""` companion is deprecated.
      event.preventDefault();
    };

    window.addEventListener("beforeunload", onBeforeUnload);

    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [anyDirty]);

  return (
    <UnsavedChangesContext.Provider value={registry}>
      {children}
      <UnsavedChangesDialog
        open={blocker.state === "blocked"}
        title={dirtyCopy?.title}
        description={dirtyCopy?.description}
        onConfirm={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      />
    </UnsavedChangesContext.Provider>
  );
};
